import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { EmojiEvents as BadgeIcon } from '@mui/icons-material';
import apiClient from '../services/api/apiClient';

interface BadgeStat {
  id: string;
  name: string;
  type: string;
  description: string;
  criteria: string;
  awarded_count: number;
  active_count: number;
  active: boolean;
}

interface BadgeSummary {
  total_types: number;
  total_awarded: number;
  total_active: number;
}

export default function BadgesPage() {
  const [badges, setBadges] = useState<BadgeStat[]>([]);
  const [summary, setSummary] = useState<BadgeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get('/admin/badges/')
      .then((res) => {
        const d = res.data?.data ?? res.data;
        setBadges(d.badges ?? []);
        setSummary(d.summary ?? null);
      })
      .catch((err) => {
        setError(err.response?.data?.message || 'Failed to load badge data');
      })
      .finally(() => setLoading(false));
  }, []);

  const avgAwarded =
    summary && summary.total_types > 0
      ? Math.round(summary.total_awarded / summary.total_types)
      : 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Badges Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Badge Types', value: summary?.total_types ?? badges.length },
          { label: 'Total Awarded',     value: summary?.total_awarded ?? '—' },
          { label: 'Currently Active',  value: summary?.total_active ?? '—' },
          { label: 'Avg Awarded / Type', value: avgAwarded },
        ].map(({ label, value }) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" variant="caption">
                  {label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 1 }}>
                  {loading ? <CircularProgress size={20} /> : value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Badges Table */}
      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.paper', borderBottom: '2px solid #f3f4f6' }}>
              <TableCell sx={{ fontWeight: 700 }}>Badge</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Criteria</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Awarded</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Active Holders</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : badges.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <BadgeIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography color="textSecondary">No badges awarded yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              badges.map((badge) => (
                <TableRow key={badge.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{badge.name}</TableCell>
                  <TableCell>
                    <Chip label={badge.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{badge.description}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {badge.criteria}
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{badge.awarded_count.toLocaleString()}</Typography>
                  </TableCell>
                  <TableCell>{badge.active_count.toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={badge.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={badge.active ? 'success' : 'default'}
                      variant="filled"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
        Badges are awarded automatically by the platform based on artisan performance metrics.
      </Typography>
    </Box>
  );
}
